import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import BACKEND_URL from '../config';

interface FeedbackExportEvent {
    id: number;
    type: string;
    sender: string;
    content: string;
    elapsed: string;
    document_name?: string | null;
    document_filename?: string | null;
    quotes?: string[];
}

interface SpotInfoRow {
    field: string;
    value: string;
}

interface SpotInfoSectionData {
    title: string;
    rows: SpotInfoRow[];
}

interface SpotInfoPayload {
    address_label: string;
    sections: SpotInfoSectionData[];
}

interface FeedbackExportResponse {
    simulation_id: number;
    simulation_token: string;
    situation_id: number | null;
    gms_id: string | null;
    spot_info: SpotInfoPayload | null;
    events: FeedbackExportEvent[];
}
// Blind labelling: AI-generated and human-suggested messages both show
// as 'Co-commander' so reviewers cannot distinguish them on the printout.
function isCoCommander(ev: FeedbackExportEvent): boolean {
    return ev.type === 'ai_operator' || ev.sender === 'ai_operator';
}

function SenderCell({ ev }: { ev: FeedbackExportEvent }) {
    if (isCoCommander(ev)) {
        return <span className="font-bold">Co-commander</span>;
    }
    if (ev.type === 'human_characteristic_estimation') {
        return <span>Menselijke kenmerkenschatting</span>;
    }
    if (ev.type === 'unit_status_update') {
        return <span>Eenheidsstatusupdate</span>;
    }
    return <span>Centralist</span>;
}

const RATING_ANNOTATIONS: Record<number, string> = {
    0: 'Dit is schadelijk voor onze operatie bij dit incident',
    3: 'Niet behulpzaam, niet schadelijk',
    5: 'Dit verbetert mijn handelingsperspectief',
};

function ratingBox(value: number) {
    return (
        <div className="flex flex-col items-center w-7 shrink-0">
            <div className="w-6 h-6 border border-gray-700 rounded-sm" />
            <span className="text-xs mt-0.5 font-medium">{value}</span>
        </div>
    );
}

function ratingLegend() {
    return (
        <div className="mt-2 space-y-0.5 text-[9px] text-gray-600 leading-snug">
            <div>
                <span className="font-semibold">0:</span> {RATING_ANNOTATIONS[0]}
            </div>
            <div>
                <span className="font-semibold">3:</span> {RATING_ANNOTATIONS[3]}
            </div>
            <div>
                <span className="font-semibold">5:</span> {RATING_ANNOTATIONS[5]}
            </div>
        </div>
    );
}

function blankLines(rows: number) {
    return (
        <div className="space-y-5 mt-2">
            {Array.from({ length: rows }).map((_, idx) => (
                <div key={idx} className="border-b border-gray-400 h-5" />
            ))}
        </div>
    );
}

function IdealCoCommanderSuggestionForm({ number }: { number: 1 | 2 }) {
    return (
        <section className="print-page-break pt-4">
            <div className="border-2 border-gray-700 rounded-md p-5 bg-white">
                <h2 className="text-xl font-bold mb-1">
                    Suggestie voor een ideaal Co-commander bericht
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                    Formulier {number} van 2 — Beschrijf een bericht dat Co-commander in deze situatie
                    had kunnen of moeten geven, en licht toe waarom dit het handelingsperspectief verbetert.
                </p>

                <div className="mb-5">
                    <div className="text-sm font-semibold mb-1">Tijd</div>
                    <p className="text-xs text-gray-500 mb-2">
                        Op welk moment in de simulatie zou dit bericht het meest waardevol zijn geweest?
                    </p>
                    {blankLines(1)}
                </div>

                <div className="mb-5">
                    <div className="text-sm font-semibold mb-1">Bericht</div>
                    <p className="text-xs text-gray-500 mb-2">
                        Formuleer het voorgestelde Co-commander bericht zoals u het aan de centralist zou willen tonen.
                    </p>
                    {blankLines(4)}
                </div>

                <div className="mb-5">
                    <div className="text-sm font-semibold mb-1">Documentbron</div>
                    <p className="text-xs text-gray-500 mb-2">
                        Welk document, protocol of naslagwerk ondersteunt deze suggestie?
                    </p>
                    {blankLines(2)}
                </div>

                <div>
                    <div className="text-sm font-semibold mb-1">
                        Waarom is dit bericht waardevol in deze situatie?
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                        Wat in de situatie maakt dit bericht relevant, tijdig of noodzakelijk voor de operatie?
                    </p>
                    {blankLines(4)}
                </div>
            </div>
        </section>
    );
}

function SpotInfoSection({ spotInfo }: { spotInfo: SpotInfoPayload | null }) {
    if (!spotInfo || spotInfo.sections.length === 0) {
        return (
            <section>
                <h2 className="text-xl font-semibold mb-3">Spot Info</h2>
                <p className="text-gray-500 italic text-sm">Geen Spot Info beschikbaar.</p>
            </section>
        );
    }

    return (
        <section className="spot-info-section">
            <h2 className="text-xl font-semibold mb-1">Spot Info</h2>
            <p className="text-sm text-gray-700 mb-4">{spotInfo.address_label}</p>
            <div className="space-y-4">
                {spotInfo.sections.map((section) => (
                    <div key={section.title}>
                        <h3 className="text-sm font-semibold text-gray-800 mb-1">{section.title}</h3>
                        {section.rows.length === 0 ? (
                            <p className="text-xs text-gray-500 italic">Geen gegevens.</p>
                        ) : (
                            <table className="w-full text-xs border border-gray-300 spot-info-table">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="text-left px-2 py-1 border-b border-gray-300 w-1/3">Veld</th>
                                        <th className="text-left px-2 py-1 border-b border-gray-300">Waarde</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {section.rows.map((row, idx) => (
                                        <tr key={`${section.title}-${row.field}-${idx}`} className="align-top">
                                            <td className="px-2 py-1 border-b border-gray-200 font-medium text-gray-700 break-words">
                                                {row.field}
                                            </td>
                                            <td className="px-2 py-1 border-b border-gray-200 text-gray-800 break-words">
                                                {row.value}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}

function AiSuggestionSource({ ev }: { ev: FeedbackExportEvent }) {
    const hasDocument = Boolean(ev.document_name || ev.document_filename);
    const quotes = ev.quotes?.filter((q) => q.trim()) ?? [];
    if (!hasDocument && quotes.length === 0) {
        return null;
    }
    return (
        <div className="mt-2 pl-3 border-l-2 border-gray-300 text-xs text-gray-700 space-y-2">
            {hasDocument && (
                <div>
                    <span className="font-semibold uppercase tracking-wide text-gray-500">Document</span>
                    {ev.document_name && (
                        <div className="mt-0.5">{ev.document_name}</div>
                    )}
                    {ev.document_filename && ev.document_filename !== ev.document_name && (
                        <div className="mt-0.5 text-gray-600 font-mono text-[11px] break-all">
                            {ev.document_filename}
                        </div>
                    )}
                </div>
            )}
            {quotes.length > 0 && (
                <div>
                    <span className="font-semibold uppercase tracking-wide text-gray-500">Citaat</span>
                    <div className="mt-1 space-y-2">
                        {quotes.map((quote, idx) => (
                            <blockquote
                                key={idx}
                                className="italic text-gray-800 whitespace-pre-wrap border-l border-gray-300 pl-2"
                            >
                                {quote}
                            </blockquote>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

const FeedbackPrint = () => {
    const { token } = useParams<{ token: string }>();
    const [data, setData] = useState<FeedbackExportResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setLoading(true);
        fetch(`${BACKEND_URL}/vnog/simulations/${token}/feedback_export/`, {
            credentials: 'include',
        })
            .then(async (res) => {
                if (!res.ok) throw new Error(`Server returned ${res.status}`);
                return res.json();
            })
            .then((json: FeedbackExportResponse) => {
                if (cancelled) return;
                setData(json);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(String(err));
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    if (loading) {
        return <div className="p-8 text-gray-700">Beoordelingsformulier laden…</div>;
    }
    if (error || !data) {
        return (
            <div className="p-8 text-red-700">
                Beoordelingsformulier laden mislukt: {error || 'onbekende fout'}
            </div>
        );
    }

    const aiEvents = data.events.filter((e) => e.type === 'ai_operator');
    return (
        <div className="bg-white min-h-screen text-gray-900">
            {/* Print-specific page setup + toolbar hiding. Kept inline so the
                route is self-contained and no global CSS is required. */}
            <style>{`
                @page { size: A4; margin: 16mm; }
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    .spot-info-table tr { break-inside: avoid; page-break-inside: avoid; }
                }
                .print-page-break { break-before: page; page-break-before: always; }
                .rating-scale { flex-wrap: nowrap !important; }
                .spot-info-table td, .spot-info-table th { padding: 2px 6px; line-height: 1.25; }
            `}</style>

            <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold">Beoordelingsformulier Co-commander</h1>
                    <p className="text-xs text-gray-500">
                        Simulatie-ID: {data.simulation_id}
                        {data.gms_id && <> · GMS: {data.gms_id}</>}
                        {data.situation_id != null && <> · Situatie-ID: {data.situation_id}</>}
                        {' · '}token {data.simulation_token}
                    </p>
                </div>
                <button
                    onClick={() => window.print()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                    Afdrukken / Opslaan als PDF
                </button>
            </div>

            <div className="max-w-4xl mx-auto px-8 py-6">
                <header className="mb-8 pb-4 border-b border-gray-300">
                    <h1 className="text-2xl font-bold">Beoordelingsformulier Co-commander</h1>
                    <div className="mt-2 text-sm text-gray-700 space-y-1">
                        <p>
                            <span className="font-semibold">Simulatie-ID:</span>{' '}
                            <span className="font-mono text-base">{data.simulation_id}</span>
                        </p>
                        {data.gms_id && (
                            <p>
                                <span className="font-semibold">GMS-nummer:</span>{' '}
                                <span className="font-mono text-base">{data.gms_id}</span>
                            </p>
                        )}
                        {data.situation_id != null && (
                            <p>
                                <span className="font-semibold">Situatie-ID:</span>{' '}
                                <span className="font-mono text-base">{data.situation_id}</span>
                            </p>
                        )}
                    </div>
                </header>

                <SpotInfoSection spotInfo={data.spot_info} />
                <section className="mt-10">
                    <h2 className="text-xl font-semibold mb-3">Kladblokregels van de meldkamer &amp; co-commander</h2>
                    {data.events.length === 0 ? (
                        <p className="text-gray-500 italic">Geen kladblokregels geregistreerd.</p>
                    ) : (
                        <table className="w-full text-sm border border-gray-300">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="text-left px-3 py-2 border-b border-gray-300 w-20">Tijd</th>
                                    <th className="text-left px-3 py-2 border-b border-gray-300 w-44">Afzender</th>
                                    <th className="text-left px-3 py-2 border-b border-gray-300">Bericht</th>
                                    <th className="text-right px-3 py-2 border-b border-gray-300 w-20">ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.events.map((ev) => (
                                    <tr key={ev.id} className="align-top">
                                        <td className="px-3 py-2 border-b border-gray-200 font-mono text-xs">{ev.elapsed}</td>
                                        <td className="px-3 py-2 border-b border-gray-200"><SenderCell ev={ev} /></td>
                                        <td className="px-3 py-2 border-b border-gray-200">
                                            <div className="whitespace-pre-wrap">{ev.content}</div>
                                            {ev.type === 'ai_operator' && <AiSuggestionSource ev={ev} />}
                                        </td>
                                        <td className="px-3 py-2 border-b border-gray-200 text-right font-mono text-xs text-gray-600">{ev.id}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>

                {aiEvents.length > 0 && (
                    <section className="mt-10 print-page-break">
                        <h2 className="text-xl font-semibold mb-3">Feedback op Co-commander</h2>
                        <p className="text-sm text-gray-600">
                            Per kladblokregel volgt er 1 feedback formulier met bijbehorende ID.
                        </p>
                    </section>
                )}

                {aiEvents.map((ev, idx) => (
                    <section
                        key={ev.id}
                        className={`mt-8 ${idx === 0 ? '' : 'print-page-break'} pt-4`}
                    >
                        <div className="border-2 border-gray-700 rounded-md p-5 bg-white">
                            <div className="flex items-baseline justify-between mb-4">
                                <h3 className="text-2xl font-bold">ID: {ev.id}</h3>
                                <span className="text-sm font-mono text-gray-700">Tijd: {ev.elapsed}</span>
                            </div>

                            <div className="mb-5">
                                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Bericht</div>
                                <blockquote className="border-l-4 border-gray-400 pl-3 italic text-gray-800 whitespace-pre-wrap">
                                    {ev.content}
                                </blockquote>
                                <AiSuggestionSource ev={ev} />
                            </div>

                            <div className="mb-5">
                                <div className="text-sm font-semibold mb-2">Beoordeling (0-5)</div>
                                <div className="flex flex-nowrap gap-3 items-start rating-scale">
                                    {[0, 1, 2, 3, 4, 5].map((n) => ratingBox(n))}
                                </div>
                                {ratingLegend()}
                            </div>

                            <div className="mb-5">
                                <div className="text-sm font-semibold mb-1">Dit ging fout</div>
                                {blankLines(3)}
                            </div>

                            <div className="mb-5">
                                <div className="text-sm font-semibold mb-1">Opmerkingen</div>
                                {blankLines(3)}
                            </div>

                            <div>
                                <div className="text-sm font-semibold mb-1">Dit ging goed</div>
                                {blankLines(3)}
                            </div>
                        </div>
                    </section>
                ))}

                <IdealCoCommanderSuggestionForm number={1} />
                <IdealCoCommanderSuggestionForm number={2} />
            </div>
        </div>
    );
};

export default FeedbackPrint;
